import { useMemo } from 'react'

import {
  getFdEarlyPrice,
  getFdWindowMs,
  useFdActiveGroup,
} from '@/buyer-portal/components/features/listing/hooks/useFdActiveGroup'
import type {
  FirstDibsGroup,
  FirstDibsWithWindowStart,
} from '@/queries/listingDetailsQuery'
import type { SearchResult } from '@/queries/listingSearchQuery'

/** A search group, which carries `windowType` once search indexes it. */
type SearchFirstDibsGroup = NonNullable<
  NonNullable<SearchResult['firstDibs']>['groups']
>[number] &
  Pick<FirstDibsGroup, 'windowType'>

/**
 * The search doc's First Dibs groups in the shape the LDP window hooks read.
 * Search already narrows them to the buyer's own groups.
 */
export const toFirstDibs = (
  firstDibs: SearchResult['firstDibs']
): FirstDibsWithWindowStart | undefined =>
  firstDibs && {
    enabled: !!firstDibs.enabled,
    groups: firstDibs.groups?.flatMap(
      ({
        buyerGroupId,
        windowStart,
        windowOffset,
        earlyPrice,
        windowType,
      }: SearchFirstDibsGroup) =>
        buyerGroupId
          ? [
              {
                buyerGroupId,
                windowStart,
                windowOffset,
                earlyPrice: earlyPrice ?? undefined,
                windowType,
              },
            ]
          : []
    ),
  }

/** The buyer's open First Dibs group on this card's listing, if any. */
const useCardFdActiveGroup = (listing: SearchResult) => {
  const firstDibs = useMemo(
    () => toFirstDibs(listing.firstDibs),
    [listing.firstDibs]
  )
  return useFdActiveGroup(firstDibs).activeGroup
}

/**
 * When the buyer's First Dibs window on this card's listing closes, or null
 * while no window is open (or the flag is off). Re-renders as the window
 * opens and closes, so the card's First Dibs UI comes and goes on its own.
 */
export const useCardFirstDibsWindowEnd = (listing: SearchResult) => {
  const activeGroup = useCardFdActiveGroup(listing)
  const endMs = activeGroup ? getFdWindowMs(activeGroup)?.endMs : undefined
  return useMemo(() => (endMs === undefined ? null : new Date(endMs)), [endMs])
}

/**
 * The buyer's early Buy Now price (dollars) while their First Dibs window is
 * open, or undefined for bid-only groups / no open window. The LDP bid card
 * switches to Buy Now at this price, so the listing card does too.
 */
export const useCardFirstDibsEarlyPrice = (listing: SearchResult) =>
  getFdEarlyPrice(useCardFdActiveGroup(listing))
